// ─── UPDATED: Tax (%) field added ─────────────────────────────────────────────
import React, { useEffect, useState, useRef, useCallback } from "react";
import { Modal, Form, Input, InputNumber, Select, DatePicker, Checkbox, Spin, message } from "antd";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { Wrapper } from "../style";
import { MakeApiCall } from '../../../../apis';

dayjs.extend(customParseFormat);

const { Option } = Select;

function AddExpense({
                      isModalVisible,
                      handleCancel,
                      handleSubmit,
                      marketplaceList,
                      userData,
                      editingExpense,
                    }) {
  const [form] = Form.useForm();

  const getMarketplaceList = (marketplaceList) =>
      marketplaceList
          .map((d, i) => {
            return {
              label: (
                  <>
                    <img
                        src={`/assets/media/domainImage/${d?.value}.png`}
                        style={{ height: "20px" }}
                        loading="lazy"
                        className="me-3"
                        onError={(e) => {
                          e.target.src = "/assets/media/domainImage/red-flag.png";
                        }}
                        alt={"Marketplace"}
                    />
                    {d?.name} <span style={{fontSize:'12px', opacity:'.5'}}>({d?.label})</span>
                  </>
              ),
              value: d?.value,
            };
          });

  const searchTimer = useRef(null);

  // States
  const [productOptions, setProductOptions] = useState([]);
  const [productLoading, setProductLoading] = useState(false);
  const [dateValue, setDateValue] = useState(null);
  const [endDateValue, setEndDateValue] = useState(null);
  const [durationValue, setDurationValue] = useState(null);
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(true);
  const [frequencyType, setFrequencyType] = useState(null);

  // ─── Pagination / Search State ────────────────────────────────────────────────
  const [productPage, setProductPage] = useState(1);
  const [productHasMore, setProductHasMore] = useState(true);
  const [productSearchQuery, setProductSearchQuery] = useState('');
  // Ref mirrors to avoid stale closures inside scroll handler
  const productPageRef = useRef(1);
  const productHasMoreRef = useRef(true);
  const productLoadingRef = useRef(false);
  const productSearchQueryRef = useRef('');

  // ─── Category States ──────────────────────────────────────────────────────────
  const [categories, setCategories] = useState([]);
  const [newCategory, setNewCategory] = useState("");
  const [categoryLoading, setCategoryLoading] = useState(false);

  // Helpers -----------------------------------------------------------------
  const sanitizeAsin = (s) => {
    if (s === null || s === undefined) return "";
    let str = String(s).trim();
    str = str.replace(/^['"TM"“”‘’`]+|['"TM"“”‘’`]+$/g, "").trim();
    return str;
  };

  const normalizeProducts = (p) => {
    if (!p && p !== 0) return [];
    if (Array.isArray(p)) {
      return p
          .map((item) => (typeof item === 'object' ? (item.asin || item.sku || item.id) : item))
          .map(sanitizeAsin)
          .filter(Boolean);
    }
    if (typeof p === 'string') {
      return p.split(',').map(sanitizeAsin).filter(Boolean);
    }
    return [String(p)].map(sanitizeAsin).filter(Boolean);
  };

  // Maps raw API record → Select option shape
  const recordToOption = (r) => ({
    label: `${r.item_name || r.sku || r.asin || r.id} (${r.sku || r.asin || ''})`,
    value: sanitizeAsin(r.asin || r.sku || r.id),
  });

  // Merges new options into a map keyed by value to avoid duplicates
  const mergeOptions = (existing, incoming) => {
    const map = new Map(existing.map(o => [o.value, o]));
    incoming.forEach(o => { if (o.value) map.set(o.value, o); });
    return Array.from(map.values());
  };

  const validateRequired = async () => {
    try {
      const required = ["name", "amount", "type", "start_date", "marketplace_ids", "category"];
      if (frequencyType !== 'Once') {
        required.push("no_of_occurrences");
      }
      const dur = form.getFieldValue('no_of_occurrences');
      if (dur === 'Until') required.push('end_date');
      await form.validateFields(required);
      setIsSubmitDisabled(false);
    } catch (e) {
      setIsSubmitDisabled(true);
    }
  };

  // API Calls ---------------------------------------------------------------
  const fetchCategories = async () => {
    setCategoryLoading(true);
    try {
      const response = await MakeApiCall('get-expense-categories?no_pagination=true', 'get', null, true, {}, userData?.token);
      if (response?.status && response?.data?.records) {
        setCategories(response.data.records);
      }
    } catch (err) {
      console.error("Error fetching categories:", err);
    } finally {
      setCategoryLoading(false);
    }
  };

  const handleAddCategory = async () => {
    const trimmed = newCategory.trim();
    if (!trimmed) return;
    try {
      const response = await MakeApiCall('add-expense-category', 'post', { name: trimmed }, true, {}, userData?.token);
      if (response?.status) {
        message.success("Category added successfully");
        setNewCategory('');
        await fetchCategories();
      } else {
        message.error(response?.message || "Failed to add category");
      }
    } catch (err) {
      message.error("An error occurred while adding the category");
    }
  };

  const handleDeleteCategory = (e, categoryId) => {
    e.stopPropagation();
    Modal.confirm({
      title: 'Delete Category',
      content: 'Are you sure you want to delete this category?',
      okText: 'Yes, Delete',
      okType: 'danger',
      cancelText: 'No',
      onOk: async () => {
        try {
          const response = await MakeApiCall(`delete-expense-category/${categoryId}`, 'delete', null, true, {}, userData?.token);
          if (response?.status) {
            message.success("Category deleted successfully");
            const currentCat = form.getFieldValue('category');
            if (currentCat === categories.find(c => c.id === categoryId)?.name) {
              form.setFieldsValue({ category: undefined });
            }
            await fetchCategories();
          } else {
            message.error(response?.message || "Failed to delete category");
          }
        } catch (err) {
          message.error("An error occurred while deleting the category");
        }
      },
    });
  };

  /**
   * Core product fetcher.
   *
   * @param {string}  query   - Search text (empty = no filter)
   * @param {number}  page    - Page number to request
   * @param {boolean} append  - true → append to existing list  false → replace
   */
  const fetchProductOptions = useCallback(async (query = '', page = 1, append = false) => {
    // Guard: skip if a fetch is already in flight for the same page
    if (productLoadingRef.current) return;

    const q = sanitizeAsin(query);
    setProductLoading(true);
    productLoadingRef.current = true;

    try {
      let url = `get-products?page=${page}&per-page=10`;
      if (q) url += `&search_text=${encodeURIComponent(q)}`;

      const response = await MakeApiCall(url, 'get', null, true, {}, userData?.token);

      if (response?.status) {
        const records = response.data?.records || [];
        const incoming = records.map(recordToOption).filter(o => o.value);

        if (append) {
          setProductOptions(prev => mergeOptions(prev, incoming));
        } else {
          setProductOptions(incoming);
        }

        // If fewer records than requested page size, we've reached the end
        const hasMore = records.length >= 10;
        setProductHasMore(hasMore);
        productHasMoreRef.current = hasMore;
        setProductPage(page);
        productPageRef.current = page;
      } else {
        if (!append) setProductOptions([]);
        setProductHasMore(false);
        productHasMoreRef.current = false;
      }
    } catch (err) {
      if (!append) setProductOptions([]);
      setProductHasMore(false);
      productHasMoreRef.current = false;
    } finally {
      setProductLoading(false);
      productLoadingRef.current = false;
    }
  }, [userData?.token]);

  /**
   * Edit-mode helper: fetch products by ASINs so pre-filled values have labels.
   * Merges fetched options with whatever is already in the list (initial load
   * may have already run) so selected values always resolve to readable labels.
   */
  const fetchProductsByAsins = async (asins = []) => {
    const cleaned = (asins || []).map(sanitizeAsin).filter(Boolean);
    if (!cleaned.length) return;
    setProductLoading(true);
    productLoadingRef.current = true;
    try {
      const calls = cleaned.map(a =>
          MakeApiCall(`get-products?search_text=${encodeURIComponent(a)}&page=1&per-page=1`, 'get', null, true, {}, userData?.token)
      );
      const results = await Promise.all(calls);
      const records = results.flatMap(r => r?.status ? (r.data?.records || []) : []);
      const fetched = records.map(recordToOption).filter(o => o.value);
      const found = new Set(fetched.map(o => o.value));
      // Fallback: ensure selected ASINs that returned no API hit still appear as options
      const fallbacks = cleaned
          .filter(a => a && !found.has(a))
          .map(a => ({ label: a, value: a }));

      setProductOptions(prev => mergeOptions(mergeOptions(prev, fetched), fallbacks));
    } catch (err) { /* ignore */ } finally {
      setProductLoading(false);
      productLoadingRef.current = false;
    }
  };

  // ─── Dropdown Event Handlers ──────────────────────────────────────────────────

  /**
   * Fires when the dropdown opens/closes.
   * On open: reset pagination state and load first page.
   */
  const onProductDropdownVisibleChange = (open) => {
    if (!open) return;

    // Preserve currently selected values as options so they don't vanish on re-open
    const selected = form.getFieldValue('products') || [];
    const selectedOptions = selected
        .map(sanitizeAsin)
        .filter(Boolean)
        .map(v => {
          const existing = productOptions.find(o => o.value === v);
          return existing || { label: v, value: v };
        });

    // Reset search / pagination
    setProductSearchQuery('');
    productSearchQueryRef.current = '';
    setProductPage(1);
    productPageRef.current = 1;
    setProductHasMore(true);
    productHasMoreRef.current = true;

    // Seed options with preserved selected values before the API call resolves
    setProductOptions(selectedOptions);

    fetchProductOptions('', 1, false).then(() => {
      // After the initial page loads, re-merge any selected options that
      // may not have appeared in the first page of results
      if (selectedOptions.length) {
        setProductOptions(prev => mergeOptions(prev, selectedOptions));
      }
    });
  };

  /**
   * Fires on search input change inside the Select.
   * Resets to page 1 and fetches with the new query.
   */
  const onProductSearch = (val) => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      const q = val || '';
      setProductSearchQuery(q);
      productSearchQueryRef.current = q;
      setProductPage(1);
      productPageRef.current = 1;
      setProductHasMore(true);
      productHasMoreRef.current = true;
      fetchProductOptions(q, 1, false);
    }, 300);
  };

  /**
   * Fires on dropdown scroll.
   * When near the bottom and more pages exist, fetch the next page.
   */
  const onProductPopupScroll = (e) => {
    const { target } = e;
    const nearBottom = target.scrollHeight - target.scrollTop - target.clientHeight < 80;

    if (nearBottom && !productLoadingRef.current && productHasMoreRef.current) {
      const nextPage = productPageRef.current + 1;
      fetchProductOptions(productSearchQueryRef.current, nextPage, true);
    }
  };

  // ─── Modal Lifecycle ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (!isModalVisible) {
      form.resetFields();
      setDateValue(null);
      setEndDateValue(null);
      setDurationValue(null);
      setIsSubmitDisabled(true);
      setProductOptions([]);
      setFrequencyType(null);
      // Reset pagination state
      setProductPage(1);
      productPageRef.current = 1;
      setProductHasMore(true);
      productHasMoreRef.current = true;
      setProductSearchQuery('');
      productSearchQueryRef.current = '';
      return;
    }

    fetchCategories();

    (async () => {
      const init = {};
      if (editingExpense?.start_date) {
        const parsed = dayjs(editingExpense.start_date, 'YYYY-MM-DD', true);
        if (parsed.isValid()) { setDateValue(parsed); init.start_date = parsed; }
      }
      if (editingExpense?.end_date) {
        const parsedEnd = dayjs(editingExpense.end_date, 'YYYY-MM-DD', true);
        if (parsedEnd.isValid()) { setEndDateValue(parsedEnd); init.end_date = parsedEnd; }
      }

      const existingDuration = editingExpense?.duration || editingExpense?.no_of_occurrences;
      if (existingDuration) {
        setDurationValue(existingDuration);
        init.no_of_occurrences = existingDuration;
      }

      if (editingExpense?.type) { setFrequencyType(editingExpense.type); }

      const coreInit = { ...editingExpense, ...init };
      delete coreInit.products;

      form.setFieldsValue(coreInit);

      const cleanedProducts = normalizeProducts(editingExpense?.products);
      if (cleanedProducts.length) {
        await fetchProductsByAsins(cleanedProducts);
        form.setFieldsValue({ products: cleanedProducts });
      }
      setTimeout(() => validateRequired(), 0);
    })();
  }, [isModalVisible, editingExpense]);

  // Handlers ----------------------------------------------------------------
  const onDateChange = (date) => {
    setDateValue(date ? date.startOf('day') : null);
    form.setFieldsValue({ start_date: date ? date.startOf('day') : null });
    validateRequired();
  };

  const onEndDateChange = (date) => {
    setEndDateValue(date ? date.startOf('day') : null);
    form.setFieldsValue({ end_date: date ? date.startOf('day') : null });
    validateRequired();
  };

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      const processedValues = {
        ...values,
        start_date: values.start_date ? values.start_date.format('YYYY-MM-DD') : null,
        end_date: values.end_date ? values.end_date.format('YYYY-MM-DD') : null,
        duration: values.no_of_occurrences,
        products: Array.isArray(values.products)
            ? values.products.map(p => sanitizeAsin(p)).filter(Boolean).join(', ')
            : '',
        currency: 'INR',
        // ── Tax: default to 0 when left blank (works for both add & edit) ────
        tax: values.tax ?? 0,
        amortize_expense_daily: values.amortize_expense_daily ? 1 : 0,
        show_under_advertising_cost: values.show_under_advertising_cost ? 1 : 0,
      };
      if (editingExpense) processedValues.id = editingExpense.id;
      await handleSubmit(processedValues, editingExpense ? 'edit' : 'add');
    } catch (error) { console.error('Validation Failed:', error); }
  };

  // ─── Render ───────────────────────────────────────────────────────────────────
  return (
      <Modal
          visible={isModalVisible}
          title={editingExpense ? 'Edit Expense' : 'Add Expense'}
          onCancel={handleCancel}
          width={600}
          destroyOnClose
          footer={[
            <button key="back" type="button" className="btn btn-light me-3" onClick={handleCancel}>Close</button>,
            <button key="submit" type="button" className="btn btn-danger" onClick={handleOk} disabled={isSubmitDisabled}>
              {editingExpense ? 'Update' : 'Save'}
            </button>
          ]}
      >
        <style>{`
        .modal_custom .ant-form-item-explain { display: none !important; } 
        
        .modal_custom .ant-form-item-has-error .ant-select-selector, 
        .modal_custom .ant-form-item-has-error .ant-input, 
        .modal_custom .ant-form-item-has-error .ant-input-number,
        .modal_custom .ant-form-item-has-error .ant-picker { 
          border-color: #ff4d4f !important; 
          box-shadow: none !important;
        }

        .modal_custom .ant-select-single .ant-select-selector, 
        .modal_custom .ant-input,
        .modal_custom .ant-input-number,
        .modal_custom .ant-picker { 
          height: 40px !important; 
          display: flex !important;
          align-items: center !important;
        }

        .modal_custom .ant-select-multiple .ant-select-selector {
          min-height: 40px !important;
          display: flex !important;
          align-items: center !important;
          padding-top: 2px !important;
          padding-bottom: 2px !important;
        }

        .modal_custom .ant-input-number-input-wrap input {
          height: 38px !important;
        }
        
        /* Category Delete Styling */
        .category-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          width: 100%;
        }
        .category-delete-btn {
          color: #ff4d4f;
          padding: 0 4px;
          cursor: pointer;
          font-weight: bold;
          font-size: 16px;
          line-height: 1;
        }
        .category-delete-btn:hover {
          color: #cf1322;
          background: rgba(255, 77, 79, 0.1);
          border-radius: 4px;
        }

        /* Products infinite scroll footer spinner */
        .product-scroll-loader {
          display: flex;
          justify-content: center;
          padding: 8px 0 4px;
          border-top: 1px solid #f0f0f0;
        }
      `}</style>
        <Wrapper>
          <div className="modal_custom">
            <Form
                form={form}
                layout="vertical"
                name="expenseForm"
                onValuesChange={(changedValues) => {
                  if (Object.prototype.hasOwnProperty.call(changedValues, 'no_of_occurrences')) {
                    setDurationValue(changedValues.no_of_occurrences);
                    if (changedValues.no_of_occurrences !== 'Until') {
                      setEndDateValue(null);
                      form.setFieldsValue({ end_date: null });
                    }
                  }
                  if (Object.prototype.hasOwnProperty.call(changedValues, 'type')) {
                    setFrequencyType(changedValues.type);
                    if (changedValues.type === 'Once') {
                      setDurationValue(null);
                      form.setFieldsValue({ no_of_occurrences: null, end_date: null });
                    }
                  }
                  setTimeout(() => validateRequired(), 0);
                }}
            >
              <div className="row g-5 mb-0 align-items-start">
                <div className="col-md-8">
                  <Form.Item name="name" className="mb-0" rules={[{ required: true }]}>
                    <Input className="form-control form-control-solid fs-7 h-40px" placeholder="Enter expense title" />
                  </Form.Item>
                </div>

                <div className="col-md-4">
                  <Form.Item name="amount" className="mb-0" rules={[{ required: true }]}>
                    <InputNumber
                        type={"number"}
                        min={0}
                        placeholder="Enter Amount"
                        style={{ width: '100%' }}
                        className="form-control-solid fs-7"
                        controls={false}
                    />
                  </Form.Item>
                </div>

                {/* ── Tax (%) ──────────────────────────────────────────────── */}
                <div className="col-md-4">
                  <Form.Item
                      name="tax"
                      className="mb-0"
                      rules={[
                        {
                          type: 'number',
                          min: 0,
                          max: 100,
                          message: 'Tax must be between 0 and 100',
                        },
                      ]}
                  >
                    <InputNumber
                        min={0}
                        max={100}
                        placeholder="Tax (%)"
                        style={{ width: '100%' }}
                        className="form-control-solid fs-7"
                        controls={false}
                        onKeyDown={(e) => {
                          if (['e', 'E', '+', '-'].includes(e.key)) {
                            e.preventDefault();
                          }
                        }}
                        onBlur={(e) => {
                          const raw = parseFloat(e.target.value);
                          if (!isNaN(raw)) {
                            form.setFieldsValue({ tax: Math.min(100, Math.max(0, raw)) });
                          }
                        }}
                    />
                  </Form.Item>
                </div>

                <div className="col-md-4">
                  <Form.Item name="type" className="mb-0" rules={[{ required: true }]}>
                    <Select placeholder="Frequency" className="fs-7">
                      <Option value="Once">Once</Option>
                      <Option value="Daily">Daily</Option>
                      <Option value="Weekly">Weekly</Option>
                      <Option value="Monthly">Monthly</Option>
                    </Select>
                  </Form.Item>
                </div>

                <div className="col-md-4">
                  <Form.Item name="start_date" className="mb-0" rules={[{ required: true }]}>
                    <DatePicker
                        className="form-control form-control-solid fs-7 h-40px w-100"
                        format="YYYY-MM-DD"
                        placeholder="Start date"
                        value={dateValue}
                        onChange={onDateChange}
                        allowClear
                    />
                  </Form.Item>
                </div>

                <div className="col-md-4">
                  <Form.Item
                      name="no_of_occurrences"
                      className="mb-0"
                      rules={[{ required: frequencyType && frequencyType !== 'Once' }]}
                  >
                    <Select placeholder="Duration" disabled={!frequencyType || frequencyType === 'Once'} className="fs-7">
                      <Option value="Until">Until</Option>
                      <Option value="Forever">Forever</Option>
                    </Select>
                  </Form.Item>

                  {durationValue === 'Until' && (
                      <div style={{ marginTop: 12 }}>
                        <Form.Item name="end_date" className="mb-0" rules={[{ required: true }]}>
                          <DatePicker
                              className="form-control form-control-solid fs-7 h-40px w-100"
                              format="YYYY-MM-DD"
                              placeholder="End date"
                              value={endDateValue}
                              onChange={onEndDateChange}
                              disabledDate={(current) => dateValue && current.isBefore(dateValue, 'day')}
                              allowClear
                          />
                        </Form.Item>
                      </div>
                  )}
                </div>

                <div className="col-md-12">
                  <Form.Item name="amortize_expense_daily" className="mb-0" valuePropName="checked">
                    <Checkbox>Amortize expense daily</Checkbox>
                  </Form.Item>
                </div>

                <div className="col-md-12">
                  <Form.Item name="show_under_advertising_cost" className="mb-0" valuePropName="checked">
                    <Checkbox>Show this expense under "Advertising cost"</Checkbox>
                  </Form.Item>
                </div>

                <div className="col-md-6">
                  <Form.Item name="category">
                    <Select
                        placeholder="Select Category"
                        loading={categoryLoading}
                        className="fs-7"
                        dropdownRender={menu => (
                            <>
                              {menu}
                              <div style={{ display: 'flex', gap: 8, padding: 8, borderTop: '1px solid #e8e8e8' }}>
                                <Input value={newCategory} onChange={e => setNewCategory(e.target.value)} onPressEnter={handleAddCategory} placeholder="Add new" size="small" />
                                <button type="button" className="btn btn-sm btn-primary" onClick={handleAddCategory}>Add</button>
                              </div>
                            </>
                        )}
                    >
                      {categories?.map(cat => (
                          <Option key={cat.id} value={cat.name}>
                            <div className="category-item">
                              <span>{cat.name}</span>
                              <span
                                  className="category-delete-btn"
                                  onClick={(e) => handleDeleteCategory(e, cat.id)}
                              >
                                &times;
                              </span>
                            </div>
                          </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </div>

                <div className="col-md-6">
                  <Form.Item name="marketplace_ids" rules={[{ required: true }]}>
                    <Select showSearch options={getMarketplaceList(marketplaceList)} allowClear placeholder="Marketplace" className="fs-7" optionFilterProp="label" />
                  </Form.Item>
                </div>

                {/* ── Enhanced Products Select ───────────────────────────────── */}
                <div className="col-md-12">
                  <Form.Item name="products" className="position-relative mb-0">
                    <Select
                        mode="multiple"
                        showSearch
                        placeholder="Search products..."
                        filterOption={false}
                        // ── New: open triggers initial page-1 load ──────────────
                        onDropdownVisibleChange={onProductDropdownVisibleChange}
                        // ── Updated: resets page & query before debounced fetch ─
                        onSearch={onProductSearch}
                        // ── New: scroll-to-bottom triggers next-page append ─────
                        onPopupScroll={onProductPopupScroll}
                        // ── Existing: spinner while any fetch is in flight ──────
                        notFoundContent={
                          productLoading
                              ? <Spin size="small" />
                              : <span style={{ color: '#999', fontSize: 13 }}>No products found</span>
                        }
                        // ── New: bottom-of-list loading indicator via dropdownRender
                        dropdownRender={(menu) => (
                            <>
                              {menu}
                              {productLoading && productPage > 1 && (
                                  <div className="product-scroll-loader">
                                    <Spin size="small" />
                                  </div>
                              )}
                            </>
                        )}
                        options={productOptions}
                        allowClear
                        className="fs-7"
                        style={{ width: '100%' }}
                    />
                  </Form.Item>
                </div>
              </div>
            </Form>
          </div>
        </Wrapper>
      </Modal>
  );
}

export default AddExpense;